// ─────────────────────────────────────────────────────────────────────────────
// SaaS & Subscription industry defaults — CLIENT COPY
//
// INTERNAL ONLY - not shown in public signup or industry selector
//
// ⚠ MIRROR OF functions/saasIndustry.js — KEEP THE TWO IN SYNC.
// Neither side can import the other: functions/ deploys on its own and cannot
// reach into src/, and create-react-app's ModuleScopePlugin forbids src/ from
// importing outside src/. This is the same deliberate duplication already used
// for BONEFLOW_INTERNAL_COMPANY_ID (functions/internalCompany.js,
// src/utils/internalCompany.js, firestore.rules). The server copy is what
// SEEDS boneflow-internal; this copy is what the app reads when it needs to
// know what a SaaS company's defaults look like (industry-aware seeding in
// CompanyContext, Settings' job-type normalization, GuidedOnboarding).
//
// This is BoneFlow's second industry vertical (after Window & Door), proving
// the 5.11 multi-industry promise: a new vertical is job types + stages + lead
// sources + cost categories + custom-field defaults, NOT a new app. It is
// hidden from public signup today (BoneFlow runs its own business on it, per
// roadmap 5.10) but built complete. It is registered as an industry pack in
// src/utils/industryPacks/saasSubscription.js, whose `isPublic` keeps it out of
// signup.
//
// STABLE, READABLE IDS EVERYWHERE. The Window & Door pack
// (industryPacks/windowDoor.js) mints `stage_<timestamp>_<random>` ids at seed
// time so each company gets its own. These types are seeded from SERVER code
// and re-seeded idempotently, so their ids must be deterministic — a re-run has
// to recognize the stage it already wrote instead of appending a second copy of
// the same pipeline.
//
// Stage categories must be values from STAGE_CATEGORIES in jobTypesUtils.js
// ('Lead', 'Sent', 'Awarded', 'In Progress', 'Completed', 'Lost', 'Pending') —
// they drive close-rate math, kanban hiding, and Analytics bucketing app-wide.
// Note 'In Progress' carries a space.
// ─────────────────────────────────────────────────────────────────────────────

export const SAAS_INDUSTRY_ID = 'saasSubscription';
export const SAAS_INDUSTRY_NAME = 'SaaS & Subscription Business';

// ── Job type 1: SaaS Account (the primary type) ──────────────────────────────
// Tracks a company relationship end to end — prospect → trial → paying
// subscriber → expansion or churn. This is the type the landing page's
// demo-request function writes every inbound lead into.
export const SAAS_JOB_TYPE_ID = 'saasAccount';
export const SAAS_JOB_TYPE_NAME = 'SaaS Account';

// The first stage — where every inbound demo request lands.
export const SAAS_FIRST_STAGE = 'Lead';

export const SAAS_STAGES = [
  { id: 'saas_lead', name: 'Lead', category: 'Lead' },
  { id: 'saas_demo_scheduled', name: 'Demo Scheduled', category: 'Sent' },
  { id: 'saas_demo_completed', name: 'Demo Completed', category: 'Sent' },
  { id: 'saas_proposal_sent', name: 'Proposal Sent', category: 'Sent' },
  { id: 'saas_trial_active', name: 'Trial Active', category: 'In Progress' },
  { id: 'saas_converting', name: 'Converting', category: 'In Progress' },
  { id: 'saas_active_client', name: 'Active Client', category: 'Awarded' },
  { id: 'saas_expansion', name: 'Expansion', category: 'Awarded' },
  { id: 'saas_at_risk', name: 'At Risk', category: 'In Progress' },
  { id: 'saas_churned', name: 'Churned', category: 'Lost' },
  // Lost stages — Lost-category stages are hidden from the kanban by default
  // and excluded from the pipeline visuals, same as every other job type's.
  { id: 'saas_lost_no_show', name: 'Lost - No Show', category: 'Lost' },
  { id: 'saas_lost_not_a_fit', name: 'Lost - Not a Fit', category: 'Lost' },
  { id: 'saas_lost_price', name: 'Lost - Price', category: 'Lost' },
  { id: 'saas_lost_competitor', name: 'Lost - Competitor', category: 'Lost' },
  { id: 'saas_lost_no_response', name: 'Lost - No Response', category: 'Lost' },
];

export const SAAS_FOLDERS = ['Demo Notes', 'Contracts', 'Onboarding Docs', 'Communications', 'Invoices'];

// ── Job type 2: Partnership ──────────────────────────────────────────────────
export const PARTNERSHIP_JOB_TYPE_ID = 'saasPartnership';
export const PARTNERSHIP_JOB_TYPE_NAME = 'Partnership';

export const PARTNERSHIP_STAGES = [
  { id: 'partner_prospect', name: 'Prospect', category: 'Lead' },
  { id: 'partner_intro_call', name: 'Introductory Call', category: 'Sent' },
  { id: 'partner_agreement_sent', name: 'Agreement Sent', category: 'Sent' },
  { id: 'partner_active', name: 'Active Partner', category: 'Awarded' },
  { id: 'partner_inactive', name: 'Inactive', category: 'Lost' },
];

export const PARTNERSHIP_FOLDERS = ['Partner Agreement', 'Communications'];

// ── Job type 3: Support Ticket ───────────────────────────────────────────────
// A job type rather than a serviceTickets record, matching how the Window &
// Door industry ships "Service" as a job type in the jobs collection.
export const SUPPORT_JOB_TYPE_ID = 'saasSupportTicket';
export const SUPPORT_JOB_TYPE_NAME = 'Support Ticket';

// Where an inbound support record lands. Named rather than read off
// SUPPORT_STAGES[0] at each call site for the same reason SAAS_FIRST_STAGE is:
// functions/internal.js writes it onto every in-app bug report, so it is a
// contract, not an incidental array position.
export const SUPPORT_FIRST_STAGE = 'Open';

export const SUPPORT_STAGES = [
  { id: 'support_open', name: 'Open', category: 'Lead' },
  { id: 'support_in_progress', name: 'In Progress', category: 'In Progress' },
  { id: 'support_awaiting_customer', name: 'Awaiting Customer', category: 'In Progress' },
  { id: 'support_resolved', name: 'Resolved', category: 'Completed' },
  { id: 'support_closed', name: 'Closed', category: 'Completed' },
];

export const SUPPORT_FOLDERS = ['Screenshots', 'Communications'];

// ── Default custom fields for the SaaS Account job type ──────────────────────
// Shape matches the app's existing custom-field model EXACTLY (see
// src/components/CustomFields.js and Settings → Custom Fields):
//   { id, label, type, order }  with type ∈ text | number | boolean | date
// plus `options: string[]` for the `select` type this industry introduces
// (CustomFieldsEdit renders it as a dropdown; CustomFieldsDisplay already
// prints the stored string).
//
// ARR is a MANUAL number field. The spec asked for "MRR × 12 if possible" — the
// custom-field model has no computed/derived concept, and inventing one for a
// single field would put per-field formula logic into a generic system.
// Auto-derivation belongs with the SaaS analytics work (see BONEFLOW_VISION.md
// → "SaaS-Specific Analytics"), which computes ARR from MRR across accounts.
//
// Cancellation Reason is only meaningful once an account reaches Churned.
// CustomFieldsDisplay already hides empty fields, so it stays invisible on
// healthy accounts without any conditional-field machinery.
//
// ARCHITECTURAL NOTE: custom fields in BoneFlow are COMPANY-level and scoped by
// ENTITY (customFields.jobs / .clients / .serviceTickets), not per job type. So
// seeding these makes them available on every job in the company, including
// Partnership and Support Ticket records. That is acceptable today (unused
// fields simply never render) and per-job-type custom fields are the natural
// future extension — the same "shared pool, per-type configuration" shape as
// roadmap 5.1's job type tabs.
export const SAAS_ACCOUNT_CUSTOM_FIELDS = [
  { id: 'saas_mrr', label: 'MRR', type: 'number', order: 0 },
  { id: 'saas_arr', label: 'ARR', type: 'number', order: 1 },
  { id: 'saas_plan_tier', label: 'Plan Tier', type: 'select', order: 2, options: ['Base', 'Growth', 'Enterprise', 'Custom'] },
  { id: 'saas_user_count', label: 'Number of Users', type: 'number', order: 3 },
  { id: 'saas_trial_end_date', label: 'Trial End Date', type: 'date', order: 4 },
  { id: 'saas_contract_end_date', label: 'Contract End Date', type: 'date', order: 5 },
  { id: 'saas_renewal_date', label: 'Renewal Date', type: 'date', order: 6 },
  { id: 'saas_churn_risk', label: 'Churn Risk', type: 'select', order: 7, options: ['Low', 'Medium', 'High'] },
  { id: 'saas_cancellation_reason', label: 'Cancellation Reason', type: 'text', order: 8 },
];

// ── Lead sources ─────────────────────────────────────────────────────────────
// { id, name, isActive, order } per leadSourcesUtils.js. Clients store the ID
// (not the display name) so a later rename never orphans historical records.
// DEMO_REQUEST_LEAD_SOURCE_ID is LOAD-BEARING: functions/internal.js stamps it
// on every inbound demo request. Never change it.
export const DEMO_REQUEST_LEAD_SOURCE_ID = 'demo_request_form';

export const SAAS_LEAD_SOURCES = [
  { id: DEMO_REQUEST_LEAD_SOURCE_ID, name: 'Demo Request Form', isActive: true, order: 0 },
  { id: 'referral', name: 'Referral', isActive: true, order: 1 },
  { id: 'cold_outreach', name: 'Cold Outreach', isActive: true, order: 2 },
  { id: 'conference', name: 'Conference / Trade Show', isActive: true, order: 3 },
  { id: 'partner', name: 'Partner', isActive: true, order: 4 },
  { id: 'organic_search', name: 'Organic Search', isActive: true, order: 5 },
  { id: 'social_media', name: 'Social Media', isActive: true, order: 6 },
  { id: 'other', name: 'Other', isActive: true, order: 7 },
];

// ── Cost categories ──────────────────────────────────────────────────────────
// { id, name, isActive, order } per costCategoriesUtils.js. Stable ids for the
// same reason: a cost entry stores the id, so read-time seeding on any client
// produces categories that resolve identically everywhere.
export const SAAS_COST_CATEGORIES = [
  // AWS, Firebase, Google Cloud, and anything else that scales with usage.
  { id: 'infrastructure', name: 'Infrastructure', isActive: true, order: 0 },
  { id: 'software_tools', name: 'Software & Tools', isActive: true, order: 1 },
  { id: 'marketing', name: 'Marketing & Advertising', isActive: true, order: 2 },
  { id: 'sales_commission', name: 'Sales Commission', isActive: true, order: 3 },
  { id: 'support_operations', name: 'Support & Operations', isActive: true, order: 4 },
  { id: 'legal_compliance', name: 'Legal & Compliance', isActive: true, order: 5 },
  { id: 'other', name: 'Other', isActive: true, order: 6 },
];

// ── Builders ─────────────────────────────────────────────────────────────────
// Return fresh copies so a caller can mutate/reorder without touching the
// module-level source of truth. (Unlike the Window & Door builder these do NOT
// mint new ids — see the STABLE IDS note at the top.)

function buildJobType(id, name, stages, folders, order, customFields) {
  return {
    id,
    name,
    // TRUE: these are this industry's shipped defaults, exactly as Replacement
    // and New Construction are Window & Door's. Settings treats isDefault types
    // as undeletable, which is the intended behavior here too.
    isDefault: true,
    basedOn: null,
    defaultStages: stages.map((s, i) => ({ ...s, order: i })),
    defaultFolders: [...folders],
    defaultTasks: [],
    defaultCustomFields: (customFields || []).map((f, i) => ({
      ...f,
      order: i,
      ...(f.options ? { options: [...f.options] } : {}),
    })),
    defaultPricingPresetId: null,
    order,
    visible: true,
  };
}

export function buildSaasAccountJobType() {
  return buildJobType(
    SAAS_JOB_TYPE_ID, SAAS_JOB_TYPE_NAME, SAAS_STAGES, SAAS_FOLDERS, 0,
    SAAS_ACCOUNT_CUSTOM_FIELDS,
  );
}

export function buildPartnershipJobType() {
  return buildJobType(
    PARTNERSHIP_JOB_TYPE_ID, PARTNERSHIP_JOB_TYPE_NAME, PARTNERSHIP_STAGES, PARTNERSHIP_FOLDERS, 1,
    [],
  );
}

export function buildSupportTicketJobType() {
  return buildJobType(
    SUPPORT_JOB_TYPE_ID, SUPPORT_JOB_TYPE_NAME, SUPPORT_STAGES, SUPPORT_FOLDERS, 2,
    [],
  );
}

export function buildSaasJobTypes() {
  return [
    buildSaasAccountJobType(),
    buildPartnershipJobType(),
    buildSupportTicketJobType(),
  ];
}

export function buildSaasLeadSources() {
  return SAAS_LEAD_SOURCES.map((s) => ({ ...s }));
}

export function buildSaasCostCategories() {
  return SAAS_COST_CATEGORIES.map((c) => ({ ...c }));
}

// The entity-keyed customFields map this industry seeds into
// companies/{id}/meta/extras.customFields. Only `jobs` is populated — the SaaS
// fields describe an ACCOUNT (a job), not the client company record.
export function buildSaasCustomFields() {
  return {
    jobs: SAAS_ACCOUNT_CUSTOM_FIELDS.map((f) => ({
      ...f,
      ...(f.options ? { options: [...f.options] } : {}),
    })),
    clients: [],
    serviceTickets: [],
  };
}
